import React from 'react';
import { useLocation } from 'react-router-dom';
import { formatCurrency } from '../services/utils';
import { CUSTOMER_SUPPORT } from '../data/customerPolicies';

const Calculate = () => {
  const location = useLocation();
  const bookingData = location.state || null;

  if (!bookingData) {
    return (
      <main>
        <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
          <div className="container">
            <h1 className="display-4 fw-bold">Booking Summary</h1>
          </div>
        </section>
        <section className="block-space">
          <div className="container text-center">
            <p className="lead">No booking data available. Please make a booking first.</p>
            <a href="/" className="btn btn-primary">Go Home</a>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      {/* Page Banner */}
      <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <div className="container">
          <h1 className="display-4 fw-bold">Booking Summary</h1>
          <p className="lead">Your ride details and fare breakdown</p>
        </div>
      </section>

      {/* Booking Summary */}
      <section className="block-space">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              {/* Status */}
              <div className="alert alert-success mb-4" role="alert">
                <i className="fa fa-check-circle me-2"></i>
                <strong>Booking Confirmed!</strong> Your booking has been successfully registered.
              </div>

              {/* Summary Card */}
              <div className="summary-card card shadow-lg mb-4">
                <div className="card-header" style={{ backgroundColor: 'var(--primary-color)' }}>
                  <h5 className="mb-0">Trip Details</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Pickup Location</h6>
                      <p className="lead">
                        <i className="fa fa-map-marker" style={{ color: 'var(--primary-color)' }}></i>
                        {bookingData.pickupLocation}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Dropoff Location</h6>
                      <p className="lead">
                        <i className="fa fa-map-marker" style={{ color: 'var(--primary-color)' }}></i>
                        {bookingData.dropoffLocation}
                      </p>
                    </div>
                  </div>

                  <hr />

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Pickup Date</h6>
                      <p>{new Date(bookingData.pickupDate).toLocaleDateString()}</p>
                    </div>
                    <div className="col-md-6">
                      {bookingData.tripType === 'roundtrip' && (
                        <>
                          <h6 className="text-muted mb-2">Return Date</h6>
                          <p>{new Date(bookingData.returnDate).toLocaleDateString()}</p>
                        </>
                      )}
                    </div>
                  </div>

                  <hr />

                  <div className="row mb-4">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Vehicle Type</h6>
                      <p>
                        <span className="badge" style={{ backgroundColor: 'var(--primary-color)' }}>
                          {bookingData.vehicle}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Trip Type</h6>
                      <p className="text-capitalize">
                        {bookingData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Passenger Details */}
              <div className="passenger-card card shadow-lg mb-4">
                <div className="card-header" style={{ backgroundColor: 'var(--secondary-color)', color: 'white' }}>
                  <h5 className="mb-0">Passenger Information</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Name</h6>
                      <p>{bookingData.name}</p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Mobile Number</h6>
                      <p>{bookingData.mobile}</p>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Adults</h6>
                      <p>{bookingData.adults}</p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted mb-2">Children</h6>
                      <p>{bookingData.children || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fare Breakdown */}
              <div className="fare-card card shadow-lg">
                <div className="card-header" style={{ backgroundColor: 'var(--primary-color)' }}>
                  <h5 className="mb-0">Fare Breakdown</h5>
                </div>
                <div className="card-body">
                  <div className="fare-row d-flex justify-content-between mb-3">
                    <span>Distance:</span>
                    <span>{bookingData.distance} km</span>
                  </div>
                  <div className="fare-row d-flex justify-content-between mb-3">
                    <span>Rate per km:</span>
                    <span>₹{bookingData.vehiclePrice}</span>
                  </div>
                  <div className="fare-row d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(bookingData.distance * bookingData.vehiclePrice)}</span>
                  </div>

                  <div className="total-fare d-flex justify-content-between align-items-center p-3" style={{ backgroundColor: 'var(--light-color)', borderRadius: '8px' }}>
                    <h5 className="mb-0">Total Fare:</h5>
                    <h4 className="mb-0" style={{ color: 'var(--primary-color)' }}>
                      {formatCurrency(bookingData.totalFare)}
                    </h4>
                  </div>

                  <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.9rem' }}>
                    <i className="fa fa-info-circle me-2"></i>
                    Toll charges and parking fees (if any) will be billed separately.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons d-flex gap-3">
                <button className="btn btn-primary btn-lg flex-grow-1">
                  <i className="fa fa-download me-2"></i> Download Receipt
                </button>
                <button className="btn btn-outline-primary btn-lg flex-grow-1">
                  <i className="fa fa-arrow-left me-2"></i> Back Home
                </button>
              </div>

              {/* Additional Info */}
              <div className="alert alert-info mt-4">
                <strong>Next Steps:</strong>
                <ul className="mb-0 mt-2">
                  <li>Our driver will contact you 30 minutes before pickup</li>
                  <li>Please be ready at the pickup location on time</li>
                  <li>Save the driver's phone number from the confirmation message</li>
                  <li>For any changes, contact us immediately on {CUSTOMER_SUPPORT.phoneDisplay}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Calculate;
